import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { TbVariablePlus } from 'react-icons/tb';

import styles from './inputWithTemplateVars.module.scss';

export type TemplateVarItem = {
  id: string;
  name: string;
  type?: string; // Optional grouping label
};

type Props = {
  className?: string;
  disabled?: boolean;
  error?: string | null;
  id?: string;
  inputType?: 'textarea' | 'text';
  onChange: ( value: string ) => void;
  value: string;
  variables: TemplateVarItem[];
  'data-testid'?: string;
};

/**
 * Standalone input component with template variable insertion functionality
 * Uses contenteditable to mimic textarea or text input behavior
 * Maintains raw value format (e.g., "Hello {{name}}") for onChange callback
 * Future: Will render template variables as styled pills
 */
export function InputWithTemplateVars( props: Props ) {
  const {
    className,
    disabled = false,
    error,
    id,
    inputType = 'textarea',
    onChange,
    value,
    variables,
    'data-testid': dataTestId
  } = props;

  const [ showDropdown, setShowDropdown ] = useState( false );
  const contentEditableRef = useRef<HTMLDivElement>( null );
  const isInsertingVariable = useRef( false );

  // Convert text with {{variables}} into HTML with styled pills
  const formatValueWithPills = ( text: string ): string => {
    // Replace {{variable}} patterns with non-editable styled spans
    // Pattern excludes { and } from variable names to avoid matching {{{var}}}
    const pillStyles = 'background-color: rgba(0, 123, 255, 0.15); border-radius: 3px; padding: 2px 4px; font-family: monospace; color: #0056b3; user-select: all; cursor: default; display: inline-block; white-space: nowrap;';
    return text.replace( /(\{\{[^{}]+\}\})/g, ( match ) => {
      return `<span style="${ pillStyles }" contenteditable="false">${ match }</span>`;
    } );
  };

  // Sync value prop to contenteditable element when it changes from outside or on mount
  React.useEffect( () => {
    // Skip if we're currently inserting a variable (let insertTemplateVarItem handle it)
    if ( isInsertingVariable.current ) return;

    if ( contentEditableRef.current ) {
      const currentText = contentEditableRef.current.innerText;
      // Only update innerHTML if the actual text content differs
      // This prevents cursor jumping when user is typing (internal updates)
      if ( currentText !== value ) {
        const formattedHTML = formatValueWithPills( value );
        contentEditableRef.current.innerHTML = formattedHTML;
      }
    }
  }, [ value ] );

  // Initial render: set innerHTML once on mount
  React.useEffect( () => {
    if ( contentEditableRef.current && !contentEditableRef.current.innerHTML ) {
      const formattedHTML = formatValueWithPills( value );
      contentEditableRef.current.innerHTML = formattedHTML;
    }
  }, [] );

  // Handle content changes from contenteditable
  const handleInput = () => {
    if ( !contentEditableRef.current || disabled ) return;

    // Extract plain text value from contenteditable (strip HTML)
    const text = contentEditableRef.current.innerText;
    onChange( text );
  };

  // Insert variable at cursor position
  const insertTemplateVarItem = ( variableName: string ) => {
    if ( !contentEditableRef.current || disabled ) return;

    const selection = window.getSelection();
    let cursorOffset = 0;

    // Get current text from DOM (not props, which might be stale)
    const currentText = contentEditableRef.current.innerText;

    // Get current cursor position as text offset
    if ( selection && selection.rangeCount > 0 ) {
      const range = selection.getRangeAt( 0 );
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents( contentEditableRef.current );
      preCaretRange.setEnd( range.endContainer, range.endOffset );
      cursorOffset = preCaretRange.toString().length;
    }

    // Insert variable at cursor position
    const variableText = `{{${ variableName }}}`;
    const before = currentText.substring( 0, cursorOffset );
    const after = currentText.substring( cursorOffset );
    const newValue = before + variableText + after;
    const newCursorOffset = cursorOffset + variableText.length;

    // Set flag to prevent useEffect from updating innerHTML
    isInsertingVariable.current = true;

    // Insert with a unique marker after the variable for cursor positioning
    const marker = '\u200B'; // Zero-width space as marker
    const valueWithMarker = before + variableText + marker + after;
    const formattedHTML = formatValueWithPills( valueWithMarker );
    contentEditableRef.current.innerHTML = formattedHTML;

    // Notify parent of value change (without marker)
    onChange( newValue );
    setShowDropdown( false );

    // Restore cursor position after the inserted variable
    requestAnimationFrame( () => {
      requestAnimationFrame( () => {
        if ( !contentEditableRef.current ) return;

        try {
          const sel = window.getSelection();
          if ( !sel ) return;

          // Find the marker in the DOM
          const walker = document.createTreeWalker(
            contentEditableRef.current,
            NodeFilter.SHOW_TEXT,
            null
          );

          let markerNode: Node | null = null;
          let markerOffset = 0;
          let node: Node | null;

          while ( ( node = walker.nextNode() ) ) {
            const text = node.textContent || '';
            const markerIndex = text.indexOf( marker );
            if ( markerIndex !== -1 ) {
              markerNode = node;
              markerOffset = markerIndex;
              // Remove the marker
              node.textContent = text.replace( marker, '' );
              break;
            }
          }

          const range = document.createRange();

          if ( markerNode ) {
            // Place cursor where the marker was
            range.setStart( markerNode, markerOffset );
          } else {
            // Fallback: place at end
            range.selectNodeContents( contentEditableRef.current );
            range.collapse( false );
          }

          range.collapse( true );
          sel.removeAllRanges();
          sel.addRange( range );
        } catch( error ) {
          console.error( 'Error restoring cursor position:', error );
        }

        contentEditableRef.current?.focus();

        // Reset flag after cursor restoration is complete
        isInsertingVariable.current = false;
      } );
    } );
  };

  // Group and sort variables
  const groupedTemplateVarItems = React.useMemo( () => {
    // Group by type
    const groups = variables.reduce( ( acc, variable ) => {
      const groupKey = variable.type || 'Ungrouped';
      if ( !acc[groupKey] ) {
        acc[groupKey] = [];
      }
      acc[groupKey].push( variable );
      return acc;
    }, {} as Record<string, TemplateVarItem[]> );

    // Sort variables within each group alphabetically
    Object.keys( groups ).forEach( key => {
      groups[key].sort( ( a, b ) => a.name.localeCompare( b.name ) );
    } );

    // Sort group keys alphabetically
    return Object.keys( groups )
      .sort( ( a, b ) => a.localeCompare( b ) )
      .map( key => ( { type: key, variables: groups[key] } ) );
  }, [ variables ] );

  return (
    <div
      className={ clsx( styles.wrapper, className, { [styles.error]: !!error } ) }
      data-error-message={ error || undefined }
    >
      <div
        ref={ contentEditableRef }
        className={ clsx( styles.input, {
          [styles.singleLine]: inputType === 'text',
          [styles.multiLine]: inputType === 'textarea',
          [styles.disabled]: disabled
        } ) }
        contentEditable={ !disabled }
        data-testid={ dataTestId }
        id={ id }
        onInput={ handleInput }
        suppressContentEditableWarning
      />
      <div
        aria-disabled={ disabled }
        className={ clsx( styles.addButton, { [styles.disabled]: disabled } ) }
        onClick={ disabled ? undefined : () => setShowDropdown( !showDropdown ) }
        role='button'
        tabIndex={ disabled ? -1 : 0 }
      >
        <TbVariablePlus /> Add Variable
      </div>
      {
        showDropdown && (
          <div className={ styles.dropdown }>
            {
              variables.length === 0
                ? (
                    <div className={ styles.noTemplateVarItems }>No variables available</div>
                  )
                : (
                    groupedTemplateVarItems.map( ( group, groupIndex ) => (
                      <div key={ group.type } className={ styles.groupWrapper }>
                        { groupedTemplateVarItems.length > 1 && (
                          <div className={ styles.groupHeading }>{ group.type }</div>
                        ) }
                        { group.variables.map( v => (
                          <div
                            key={ v.id }
                            className={ styles.dropdownItem }
                            onClick={ () => insertTemplateVarItem( v.name ) }
                            role='button'
                            tabIndex={ 0 }
                          >
                            { v.name }
                          </div>
                        ) ) }
                      </div>
                    ) )
                  )
            }
          </div>
        )
      }
    </div>
  );
}
